<script setup lang="ts">
import { ref } from 'vue';
import { logger } from '@/utils/logger';

interface Particle {
  style: {
    left: string;
    backgroundColor: string;
    animationDelay: string;
    animationDuration: string;
    '--angle': string;
    '--velocity': string;
    '--spread': string;
  };
}

const showConfetti = ref(false);
const particles = ref<Particle[]>([]);

const colors = [
  'var(--primary-color)',
  '#10b981', // emerald
  '#f59e0b', // amber
  '#ef4444', // red
  '#8b5cf6', // violet
  '#06b6d4', // cyan
];

const createParticles = (): void => {
  const particleCount = 60;
  const newParticles: Particle[] = [];

  for (let i = 0; i < particleCount; i++) {
    // Create cannon effect - particles start from bottom-left and spread out
    const angle = Math.random() * 60 + 15; // 15-75 degrees (upward spread)
    const velocity = Math.random() * 0.5 + 0.5; // 0.5-1.0 velocity multiplier
    const spread = Math.random() * 40; // horizontal spread

    newParticles.push({
      style: {
        left: '0px', // Start from bottom-left
        backgroundColor: colors[Math.floor(Math.random() * colors.length)],
        animationDelay: `${Math.random() * 0.3}s`, // Faster initial burst
        animationDuration: `${Math.random() * 1.5 + 2.5}s`,
        '--angle': `${angle}deg`,
        '--velocity': velocity.toString(),
        '--spread': `${spread}vw`,
      },
    });
  }

  particles.value = newParticles;
};

const triggerConfetti = (): void => {
  // Only trigger confetti randomly (1 in 6 chance)
  const randomChance = Math.random();
  if (randomChance > 1 / 5) {
    logger.debug(`Confetti skipped - random chance ${randomChance.toFixed(3)} > 0.167`);
    return;
  }

  logger.info(`Confetti triggered! - random chance ${randomChance.toFixed(3)} <= 0.167`);
  createParticles();
  showConfetti.value = true;

  // Hide confetti after animation completes
  setTimeout(() => {
    showConfetti.value = false;
    particles.value = [];
  }, 4000);
};

// Expose the trigger function
defineExpose({
  triggerConfetti,
});
</script>

<template>
  <Teleport to="body">
    <div v-if="showConfetti" class="confetti-container">
      <div
        v-for="(particle, index) in particles"
        :key="index"
        class="confetti-particle"
        :style="particle.style"
      ></div>
    </div>
  </Teleport>
</template>

<style scoped>
.confetti-container {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 10000;
  overflow: hidden;
}

.confetti-particle {
  position: absolute;
  bottom: 20px; /* Start from bottom */
  left: 20px; /* Start from left corner */
  width: 8px;
  height: 8px;
  border-radius: 2px;
  animation: confetti-cannon linear forwards;

  /* Use CSS custom properties for dynamic values */
  --angle: 45deg;
  --velocity: 1;
  --spread: 20vw;
}

@keyframes confetti-cannon {
  0% {
    transform: translateX(0) translateY(0) rotate(0deg);
    opacity: 1;
  }

  20% {
    opacity: 1;
  }

  100% {
    transform: translateX(calc(var(--spread) * var(--velocity)))
      translateY(calc(-80vh * var(--velocity))) rotate(calc(720deg * var(--velocity)));
    opacity: 0;
  }
}

/* Particle shape variations */
.confetti-particle:nth-child(2n) {
  width: 6px;
  height: 12px;
  border-radius: 6px;
}

.confetti-particle:nth-child(3n) {
  width: 10px;
  height: 6px;
  border-radius: 2px;
}

.confetti-particle:nth-child(4n) {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.confetti-particle:nth-child(5n) {
  width: 4px;
  height: 4px;
  border-radius: 1px;
}

/* Create different trajectory groups for more realistic cannon spread */
.confetti-particle:nth-child(6n + 1) {
  animation-name: confetti-cannon-low;
}

.confetti-particle:nth-child(6n + 2) {
  animation-name: confetti-cannon-mid;
}

.confetti-particle:nth-child(6n + 3) {
  animation-name: confetti-cannon-high;
}

.confetti-particle:nth-child(6n + 4) {
  animation-name: confetti-cannon-wide;
}

.confetti-particle:nth-child(6n + 5) {
  animation-name: confetti-cannon-far;
}

/* Different trajectory animations */
@keyframes confetti-cannon-low {
  0% {
    transform: translateX(0) translateY(0) rotate(0deg);
    opacity: 1;
  }
  100% {
    transform: translateX(calc(var(--spread) * 0.6)) translateY(calc(-40vh * var(--velocity)))
      rotate(calc(360deg * var(--velocity)));
    opacity: 0;
  }
}

@keyframes confetti-cannon-mid {
  0% {
    transform: translateX(0) translateY(0) rotate(0deg);
    opacity: 1;
  }
  100% {
    transform: translateX(calc(var(--spread) * 0.8)) translateY(calc(-60vh * var(--velocity)))
      rotate(calc(540deg * var(--velocity)));
    opacity: 0;
  }
}

@keyframes confetti-cannon-high {
  0% {
    transform: translateX(0) translateY(0) rotate(0deg);
    opacity: 1;
  }
  100% {
    transform: translateX(calc(var(--spread) * 1.2)) translateY(calc(-85vh * var(--velocity)))
      rotate(calc(900deg * var(--velocity)));
    opacity: 0;
  }
}

@keyframes confetti-cannon-wide {
  0% {
    transform: translateX(0) translateY(0) rotate(0deg);
    opacity: 1;
  }
  100% {
    transform: translateX(calc(var(--spread) * 1.5)) translateY(calc(-50vh * var(--velocity)))
      rotate(calc(720deg * var(--velocity)));
    opacity: 0;
  }
}

@keyframes confetti-cannon-far {
  0% {
    transform: translateX(0) translateY(0) rotate(0deg);
    opacity: 1;
  }
  100% {
    transform: translateX(calc(var(--spread) * 2)) translateY(calc(-70vh * var(--velocity)))
      rotate(calc(1080deg * var(--velocity)));
    opacity: 0;
  }
}

/* Add some physics-like gravity effect */
@media (prefers-reduced-motion: no-preference) {
  .confetti-particle {
    animation-timing-function: cubic-bezier(0.25, 0.46, 0.45, 0.94);
  }
}
</style>
